import { useMemo } from 'react';
import { Plus, ShoppingCart } from 'lucide-react';
import { EVENT_KIND_META, EVENT_SEVERITY_RING, shortDate } from '@/components/manufacturing/planning/shared';
import { addDays, dayOfWeek, endOfMonth, type IsoDate } from '@/features/manufacturing/planning/dates';
import type { VendorOrderGroup } from '@/features/manufacturing/planning/procurementCalendar';
import type { CalendarEvent, PlanningModel } from '@/features/manufacturing/planning/types';
import { formatCurrency } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { GROUP_SEVERITY_CHIP, longDate } from './shared';

interface ProcurementMonthGridProps {
  model: PlanningModel;
  monthStart: IsoDate;
  groupsByDay: Map<IsoDate, VendorOrderGroup[]>;
  eventsByDay: Map<IsoDate, CalendarEvent[]>;
  canCreate: boolean;
  onOpenDay: (date: IsoDate) => void;
  onCreatePo: (group: VendorOrderGroup) => void;
}

const WEEK_HEADER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** Chips drawn before a cell collapses into "+N more". */
const MAX_VISIBLE = 4;

function GroupChip({
  group,
  canCreate,
  onOpen,
  onCreate,
}: {
  group: VendorOrderGroup;
  canCreate: boolean;
  onOpen: () => void;
  onCreate: () => void;
}) {
  const parts = `${group.orders.length} ${group.orders.length === 1 ? 'part' : 'parts'}`;
  const money = group.totalCents > 0 ? formatCurrency(group.totalCents) : group.linesWithoutCost > 0 ? 'no cost' : '';
  const deadline = group.latestDate && group.latestDate !== group.date ? `by ${shortDate(group.latestDate)}` : null;
  const showCreate = canCreate && !group.unassignable;
  return (
    <div
      className={cn(
        'flex items-stretch overflow-hidden rounded-md border text-[11px] leading-tight transition-colors',
        GROUP_SEVERITY_CHIP[group.severity],
        group.unassignable && 'border-dashed',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        title={`${group.vendorName} · ${parts}${money ? ` · ${money}` : ''}${deadline ? ` · ${deadline}` : ''}`}
        className="min-w-0 flex-1 px-1.5 py-1 text-left hover:bg-white/30"
      >
        <span className="flex items-center gap-1">
          <ShoppingCart className="h-3 w-3 shrink-0" />
          <span className="truncate font-medium">{group.vendorName}</span>
        </span>
        <span className="block truncate text-[10px] opacity-80">
          {[parts, money, deadline].filter(Boolean).join(' · ')}
        </span>
      </button>
      {showCreate && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCreate();
          }}
          title={`Create draft PO for ${group.vendorName}`}
          aria-label={`Create draft PO for ${group.vendorName}`}
          className="flex shrink-0 items-center gap-0.5 border-l border-current/20 px-1.5 text-[10px] font-semibold hover:bg-white/40"
        >
          <Plus className="h-3 w-3" />
          PO
        </button>
      )}
    </div>
  );
}

function EventChip({ event, onClick }: { event: CalendarEvent; onClick: () => void }) {
  const meta = EVENT_KIND_META[event.kind];
  const Icon = meta.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      title={event.detail ?? event.title}
      className={cn(
        'flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[11px] leading-tight transition-colors hover:brightness-95',
        meta.chip,
        EVENT_SEVERITY_RING[event.severity],
      )}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{event.title}</span>
    </button>
  );
}

/**
 * The month at full width: vendor orders first (each with its own
 * "+PO" button), then deadlines, run-outs and arrivals. Click a day for
 * everything on it.
 */
export function ProcurementMonthGrid({
  model,
  monthStart,
  groupsByDay,
  eventsByDay,
  canCreate,
  onOpenDay,
  onCreatePo,
}: ProcurementMonthGridProps) {
  const monthEnd = endOfMonth(monthStart);
  const cells = useMemo(() => {
    const dow = dayOfWeek(monthStart);
    let d = addDays(monthStart, dow === 0 ? -6 : 1 - dow);
    const out: IsoDate[] = [];
    while (d <= monthEnd || out.length % 7 !== 0) {
      out.push(d);
      d = addDays(d, 1);
      if (out.length >= 42) break;
    }
    return out;
  }, [monthStart, monthEnd]);
  const weeks = cells.length / 7;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-border bg-card">
      <div className="grid shrink-0 grid-cols-7 border-b border-border bg-secondary/40 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {WEEK_HEADER.map((label) => (
          <div key={label} className="py-2">
            {label}
          </div>
        ))}
      </div>
      <div
        className="grid flex-1 grid-cols-7"
        style={{ gridTemplateRows: `repeat(${weeks}, minmax(9rem, 1fr))` }}
      >
        {cells.map((date, index) => {
          const inMonth = date >= monthStart && date <= monthEnd;
          const groups = groupsByDay.get(date) ?? [];
          const events = eventsByDay.get(date) ?? [];
          const working = model.calendar.isWorkingDay(date);
          const isToday = date === model.today;
          const isPast = date < model.today;
          const beyondHorizon = date > model.horizonEnd;
          const dayTotal = groups.reduce((acc, g) => acc + g.totalCents, 0);

          const visibleGroups = groups.slice(0, MAX_VISIBLE);
          const visibleEvents = events.slice(0, Math.max(0, MAX_VISIBLE - visibleGroups.length));
          const hidden = groups.length - visibleGroups.length + (events.length - visibleEvents.length);
          const lastColumn = index % 7 === 6;
          const lastRow = index >= cells.length - 7;

          return (
            <div
              key={date}
              className={cn(
                'flex min-h-0 flex-col border-border p-1.5',
                !lastColumn && 'border-r',
                !lastRow && 'border-b',
                !inMonth && 'bg-muted/30 text-muted-foreground',
                inMonth && !working && 'bg-secondary/40',
                inMonth && (isPast || beyondHorizon) && 'opacity-60',
              )}
              title={beyondHorizon ? 'Beyond the planning horizon' : undefined}
            >
              <div className="mb-1 flex shrink-0 items-center justify-between gap-1">
                <button
                  type="button"
                  onClick={() => onOpenDay(date)}
                  aria-label={`Open ${longDate(date)}`}
                  className={cn(
                    'flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-xs tabular-nums hover:bg-secondary',
                    isToday && 'bg-info font-semibold text-linen hover:bg-info',
                  )}
                >
                  {Number(date.slice(8, 10))}
                </button>
                {dayTotal > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(date)}
                    className="truncate text-[10px] font-medium tabular-nums text-muted-foreground hover:text-foreground"
                    title={`${formatCurrency(dayTotal)} to order on ${longDate(date)}`}
                  >
                    {formatCurrency(dayTotal)}
                  </button>
                )}
              </div>
              <div className="min-h-0 flex-1 space-y-1 overflow-hidden">
                {visibleGroups.map((group) => (
                  <GroupChip
                    key={group.key}
                    group={group}
                    canCreate={canCreate}
                    onOpen={() => onOpenDay(date)}
                    onCreate={() => onCreatePo(group)}
                  />
                ))}
                {visibleEvents.map((event) => (
                  <EventChip key={event.id} event={event} onClick={() => onOpenDay(date)} />
                ))}
                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(date)}
                    className="px-1 text-[11px] text-muted-foreground hover:underline"
                  >
                    +{hidden} more
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
